import { DataField } from "@iventis/domain-model/model/dataField";
import { DcasConfig } from "@iventis/domain-model/model/dcasConfig";
import { ScheduleConfigEventBlockType } from "@iventis/domain-model/model/scheduleConfigEventBlockType";
import { StyledFieldLabel, styled } from "@iventis/styles";
import { Content } from "@iventis/translations";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { ScheduleConfigVenueLocationType } from "@iventis/domain-model/model/scheduleConfigVenueLocationType";
import { Switch } from "@mui/material";
import { MultipleValueTextInput } from "@iventis/components";
import React, { FunctionComponent, useMemo } from "react";

import { v4 as uuid } from "uuid";
import { EditEventBlockType } from "./edit-event-block-type";
import { createDefaultEventBlockType, convertDataFieldToEventBlockTypeDataField } from "./edit-event-block-type-helpers";
import { convertDataFieldsToDcasDataFields, getDeletedDcasDataFieldIds, getDcasDataFields } from "./edit-dcas.helpers";
import { EditScheduleConfigContainer } from "./edit-event-block-types";

interface EditDcasProps {
    dataFields: DataField[];
    dcasConfig: DcasConfig;
    eventBlockTypes: ScheduleConfigEventBlockType[];
    venueTypes: ScheduleConfigVenueLocationType[];
    onDcasChange: (dcasConfig: DcasConfig) => void;
    onEventBlockChange: (eventBlocks: ScheduleConfigEventBlockType[]) => void;
    onDataFieldsChange: (dataFields: DataField[]) => void;
}

export const EditDcas: FunctionComponent<EditDcasProps> = ({ dataFields, dcasConfig, eventBlockTypes, venueTypes, onDataFieldsChange, onDcasChange, onEventBlockChange }) => {
    const translate = useIventisTranslate();

    const dcasEventBlock = useMemo(() => eventBlockTypes.find(({ calendarLevel }) => calendarLevel === null), [eventBlockTypes]);

    // Check for empty string as we don't want to show a event block with no name
    const eventBlockInDcas = useMemo(() => eventBlockTypes.filter(({ dcasCreationAllowed, name }) => dcasCreationAllowed && name !== ""), [eventBlockTypes]);

    const dcasDataFields = useMemo(() => dataFields.filter(({ id }) => dcasConfig.dataFields.some(({ dataFieldId }) => dataFieldId === id)), [dcasConfig.dataFields, dataFields]);

    const availableDatafieldsForDcas = useMemo(
        () => dataFields.filter((datafield) => eventBlockTypes.flatMap((ebt) => ebt.dataFields).some((df) => df.dataFieldId === datafield.id)),
        [eventBlockTypes, dataFields]
    );

    // onChange handlers

    const onEventBlockDataFieldsChange = (updatedDataFields: DataField[]) => {
        // Get the dcas datafields, includes newly created ones
        const dcasDataFields = getDcasDataFields(dataFields, updatedDataFields, dcasEventBlock.dataFields);
        // Get the deleted datafields
        const deletedDataFieldIds = getDeletedDcasDataFieldIds(dataFields, updatedDataFields);
        // Convert dcas datafields to event block datafield type
        onDcasEventBlockChange({
            ...dcasEventBlock,
            dataFields: dcasDataFields.map((df, index) => convertDataFieldToEventBlockTypeDataField(df, updatedDataFields.length + index)),
        });
        // Work out the datafields which aren't in the dcas
        const nonDcasDataFields = dataFields.filter((df) => !dcasDataFields.some(({ id }) => df.id === id) && !deletedDataFieldIds.includes(df.id));
        onDataFieldsChange([...nonDcasDataFields, ...dcasDataFields]);
    };

    const onDcasEventBlockChange = (configChange: ScheduleConfigEventBlockType) => {
        onEventBlockChange([...eventBlockTypes.filter(({ id }) => id !== configChange.id), configChange]);
    };

    const onDcasEventBlockSelected = (eventBlocks: ScheduleConfigEventBlockType[]) => {
        const eventBlockIdsInDcas = eventBlocks.map(({ id }) => id);
        const updatedEventBlock = eventBlockTypes.map((eventBlock) => ({ ...eventBlock, dcasCreationAllowed: eventBlockIdsInDcas.includes(eventBlock.id) }));
        onEventBlockChange(updatedEventBlock);
    };

    const onDcasEventBlockToggleChange = (checked: boolean) => {
        if (checked) {
            const eventBlock = createDefaultEventBlockType({ calendarLevel: null, dcasCreationAllowed: true });
            onEventBlockChange([...eventBlockTypes, eventBlock]);
        } else {
            onEventBlockChange([...eventBlockTypes.filter((eventBlock) => eventBlock.id !== dcasEventBlock.id)]);
        }
    };

    const onEnableDoneToggleChange = (checked: boolean) => {
        onDcasChange({ ...dcasConfig, enableDone: checked });
    };

    const onDcasDataFieldChange = (updatedDcasDataFields: DataField[]) => {
        const parsedUpdatedDataFields = updatedDcasDataFields.map((df) => ({ ...df, id: df.id ?? uuid(), customerIdentifierName: df.customerIdentifierName ?? df.name }));
        onDcasChange({ ...dcasConfig, dataFields: convertDataFieldsToDcasDataFields(parsedUpdatedDataFields) });
    };

    return (
        <Container>
            <div>
                <MultipleValueTextInput
                    selectedValues={eventBlockInDcas}
                    values={eventBlockTypes}
                    onChange={onDcasEventBlockSelected}
                    title={translate(Content.settings.scheduleConfig.dcasEventBlockCalendar)}
                    getLabel={(eventBlock) => eventBlock.name}
                    sectionalMargin={false}
                />
            </div>
            <div>
                <MultipleValueTextInput
                    selectedValues={dcasDataFields}
                    values={availableDatafieldsForDcas}
                    onChange={onDcasDataFieldChange}
                    title={translate(Content.settings.scheduleConfig.dcasEventBlockAttributes)}
                    getLabel={(df) => df.name}
                    dataTestId="dcas-datafields"
                />
            </div>
            <div className="toggle">
                <StyledFieldLabel>{translate(Content.settings.scheduleConfig.dcasEnableDone)}</StyledFieldLabel>
                <Switch data-testid="checkbox-enable-done" color="primary" onChange={(_, checked) => onEnableDoneToggleChange(checked)} checked={dcasConfig.enableDone} />
            </div>
            <div className="toggle">
                <StyledFieldLabel>{translate(Content.settings.scheduleConfig.dcasEventBlockQuestion)}</StyledFieldLabel>
                <Switch
                    data-testid="checkbox-event-block"
                    color="primary"
                    onChange={(_, checked) => onDcasEventBlockToggleChange(checked)}
                    checked={dcasEventBlock !== undefined}
                />
            </div>
            {dcasEventBlock && (
                <EditEventBlockType
                    eventBlockType={dcasEventBlock}
                    eventBlockTypes={eventBlockTypes}
                    venueTypes={venueTypes}
                    dataFields={dataFields}
                    onValueChange={onDcasEventBlockChange}
                    onDataFieldsChange={onEventBlockDataFieldsChange}
                    dcasEventBlock
                />
            )}
        </Container>
    );
};

const Container = styled(EditScheduleConfigContainer)`
    .toggle {
        display: flex;
        align-items: center;
        justify-content: flex-start;

        label {
            margin-bottom: 0px;
        }
    }
`;
